"use client"
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react';
import { storage, clearUserData, safeSetItem, safeGetItem } from './storage';

export interface UserData {
  name?: string;
  email?: string;
  mobile?: string;
  _id?: string;
}

export interface LogoutOptions {
  redirectTo?: string;
  showConfirmation?: boolean;
  onConfirm?: () => void;
}

export interface LoginOptions {
  phone: string;
  token: string;
  userData?: UserData;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface AuthState {
  isLoggedIn: boolean;
  loading: boolean;
  userToken: string | null;
  userPhone: string | null;
}

/**
 * Simplified authentication hook
 */
export const useAuth = (): AuthState => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoggedIn: false,
    loading: true,
    userToken: null,
    userPhone: null,
  });

  const checkAuthStatus = async () => {
    try {
      // Validate token with server
      const isValidToken = await validateAndClearInvalidToken();
      
      if (!isValidToken) {
        setAuthState({
          isLoggedIn: false,
          loading: false,
          userToken: null,
          userPhone: null,
        });
        return;
      }

      const loginDetails = storage.getLoginDetails();
      
      setAuthState({
        isLoggedIn: true,
        loading: false,
        userToken: loginDetails.token,
        userPhone: loginDetails.phone,
      });
    } catch {
      setAuthState({
        isLoggedIn: false,
        loading: false,
        userToken: null,
        userPhone: null,
      });
    }
  };

  useEffect(() => {
    // Check auth status on mount
    checkAuthStatus();

    // Listen for storage changes (login/logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'login_details') {
        checkAuthStatus();
      }
    };

    // Listen for custom events (for same-tab login/logout)
    const handleAuthChange = () => {
      checkAuthStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  return authState;
};

/**
 * Helper function to trigger auth change event
 */
export const triggerAuthChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('authChange'));
  }
};

/**
 * Check if user profile is complete (has name and email)
 */
export const isUserProfileComplete = (): boolean => {
  try {
    const userData = getUserData() as UserData;
    return !!(userData?.name && userData?.email && userData?._id);
  } catch {
    return false;
  }
};

/**
 * Get user profile completion status
 */
export const getUserProfileStatus = (): {
  isComplete: boolean;
  missingFields: string[];
  userData: UserData | null;
} => {
  try {
    const userData = getUserData() as UserData;
    const missingFields = [];
    
    if (!userData?.name) missingFields.push('name');
    if (!userData?.email) missingFields.push('email');
    if (!userData?._id) missingFields.push('id');
    
    return {
      isComplete: missingFields.length === 0,
      missingFields,
      userData
    };
  } catch {
    return {
      isComplete: false,
      missingFields: ['name', 'email', 'id'],
      userData: null
    };
  }
};

/**
 * Verify that user data was properly stored
 */
export const verifyStoredUserData = (): UserData | null => {
  try {
    const userData = getUserData() as UserData;
    
    if (userData && userData.name && userData._id) {
      return userData;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Simplified login function with better error handling
 */
export const login = async (options: LoginOptions) => {
  const { phone, token, userData, onSuccess, onError } = options;

  try {
    // Basic validation
    if (!phone || !token) {
      throw new Error('Phone and token are required for login');
    }

    // Store login details
    await storage.setLoginDetails({ phone, token });

    // Store user data if provided
    if (userData) {
      safeSetItem('user_data', userData);
    }

    // Trigger UI update
    triggerAuthChange();

    // Call success callback
    onSuccess?.();

    return { success: true };
  } catch (error) {
    onError?.(error as Error);
    throw error;
  }
};

/**
 * Simplified logout function
 */
export const logout = async (options: LogoutOptions = {}) => {
  const { redirectTo = '/', showConfirmation = false, onConfirm } = options;

  try {
    // Show confirmation if requested
    if (showConfirmation) {
      const confirmed = window.confirm('Are you sure you want to logout?');
      if (!confirmed) return;
    }

    // Call onConfirm callback
    onConfirm?.();

    // Clear all auth data
    await storage.clearLoginDetails();
    clearUserData();

    // Clear session storage
    try {
      sessionStorage.clear();
    } catch (error) {
      // console.warn('Failed to clear sessionStorage:', error);
    }

    // Trigger UI update
    triggerAuthChange();

    // Redirect
    if (typeof window !== 'undefined') {
      window.location.href = redirectTo;
    }
  } catch (error) {
    // console.error('Error during logout:', error);
    // Fallback redirect
    if (typeof window !== 'undefined') {
      window.location.href = redirectTo;
    }
  }
};

/**
 * Simplified authentication check
 */
export const isAuthenticated = (): boolean => {
  return validateTokenBasic();
};

/**
 * Get current user token
 */
export const getAuthToken = (): string | null => {
  try {
    const loginDetails = storage.getLoginDetails();
    return loginDetails.token || null;
  } catch (error) {
    return null;
  }
};

/**
 * Get current user phone
 */
export const getUserPhone = (): string | null => {
  try {
    const loginDetails = storage.getLoginDetails();
    return loginDetails.phone || null;
  } catch (error) {
    return null;
  }
};

/**
 * Get user data from localStorage
 */
export const getUserData = (): unknown => {
  return safeGetItem('user_data');
};

/**
 * Set user data in localStorage
 */
export const setUserData = (userData: unknown): void => {
  safeSetItem('user_data', userData);
};

/**
 * Force logout for expired/invalid tokens
 */
export const forceLogout = async (reason: string = 'Session expired') => {
  
  try {
    await storage.clearLoginDetails();
    clearUserData();
    
    // Clear session storage
    try {
      sessionStorage.clear();
    } catch (error) {
      // console.warn('Failed to clear sessionStorage:', error);
    }

    triggerAuthChange();

    // Redirect to home
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  } catch (error) {
    // console.error('Error during force logout:', error);
    // Fallback redirect
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  }
};

/**
 * Basic token validation without server check (fallback)
 */
export const validateTokenBasic = (): boolean => {
  try {
    const loginDetails = storage.getLoginDetails();
    
    // Basic validation
    const hasToken = loginDetails.token && 
                   loginDetails.token.trim() && 
                   loginDetails.token.length > 10 &&
                   loginDetails.token !== 'undefined' &&
                   loginDetails.token !== 'null';
    
    if (!hasToken) {
      if (loginDetails.token) {
        storage.clearLoginDetails();
      }
      return false;
    }

    return true;
  } catch (error) {
    try {
      storage.clearLoginDetails();
    } catch (clearError) {
      // console.error('Error clearing corrupted login details:', clearError);
    }
    return false;
  }
};

/**
 * Simplified token validation with server check
 */
export const validateAndClearInvalidToken = async (): Promise<boolean> => {
  try {
    const loginDetails = storage.getLoginDetails();
    
    // Basic validation
    const hasToken = loginDetails.token && 
                   loginDetails.token.trim() && 
                   loginDetails.token.length > 10 &&
                   loginDetails.token !== 'undefined' &&
                   loginDetails.token !== 'null';
    
    if (!hasToken) {
      if (loginDetails.token) {
        storage.clearLoginDetails();
      }
      return false;
    }

    // Server validation - check if token is still valid
    try {
      const response = await fetch('/api/auth/validate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${loginDetails.token}`
        }
      });

      if (!response.ok) {
        storage.clearLoginDetails();
        triggerAuthChange();
        return false;
      }

      return true;
    } catch (error) {
      // console.error('Error validating token with server:', error);
      // If server validation fails, fall back to basic validation
      return validateTokenBasic();
    }
  } catch (error) {
    try {
      storage.clearLoginDetails();
    } catch (clearError) {
      // console.error('Error clearing corrupted login details:', clearError);
    }
    return false;
  }
};

/**
 * Clear all authentication data
 */
export const forceClearAuth = async (): Promise<void> => {
  try {
    await storage.clearLoginDetails();
    localStorage.removeItem('user_data');
    localStorage.removeItem('profile_data');
    localStorage.removeItem('auth_token');
    
    triggerAuthChange();
  } catch (error) {
    // console.error('Error clearing auth data:', error);
  }
};

// Debug functions for development
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).debugAuth = () => {
    try {
      const loginDetails = storage.getLoginDetails();
    } catch (error) {
    }
  };

  (window as unknown as Record<string, unknown>).clearAuth = async () => {
    await forceClearAuth();
  };
} 